import { useState } from 'react';

import { Answer, QuestionAttachment } from '@types';
import { showAttachment } from '@features/homeworks/utils';
import Spinner from '@components/spinner/Spinner';
import safeParseHtml from '@utils/safeParseHtml';
import { useIntl } from 'react-intl';

interface MSQProps {
  question: any;
  onInputChange: (value: string[]) => void;
  disableInput?: boolean;
  selectedAnswer?: string[];
}

const MSQ = ({
  question,
  onInputChange,
  disableInput = false,
  selectedAnswer = []
}: MSQProps) => {
  const intl = useIntl();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const checkboxSelected = (value: string) =>
    selectedAnswer.some((item) => item === value);

  const handleSelect = (answer: Answer) => {
    if (disableInput || !answer.id) return;

    if (checkboxSelected(answer.id)) {
      onInputChange(selectedAnswer.filter((item) => item !== answer.id));
    } else {
      onInputChange([...selectedAnswer, answer.id]);
    }
  };

  const isGridView = false;

  return (
    <div className={isGridView ? 'sm:grid sm:grid-cols-2' : ''}>
      <div className="w-full">
        {question?.questionDetails && (
          <>
            <div className="mb-3 w-fit text-xl font-bold text-primary">
              {question?.questionDetails?.title}
            </div>

            <div className="mb-12 w-full text-lg font-semibold">
              {safeParseHtml(question?.questionDetails?.text ?? '')}
            </div>

            <div className="mb-3 w-fit text-xl font-bold text-primary">
              {intl.formatMessage({ id: 'app.question' })}
            </div>
          </>
        )}

        <div className="mb-12 w-full text-lg font-semibold">
          {safeParseHtml(question?.question ?? '')}
        </div>

        <div className="mx-1 flex w-full flex-col gap-4">
          {question?.questionAttachments?.map(
            (attachment: QuestionAttachment) => (
              <div key={attachment.id} className="w-full">
                {showAttachment(attachment, true)}
              </div>
            )
          )}
        </div>
      </div>

      <div
        className={`${
          isGridView
            ? 'sm:-mt-9 sm:ps-10 sm:overflow-visible sm:max-h-none'
            : 'mt-4'
        } ${
          question?.questionDetails?.title ? 'sm:!mt-0' : ''
        } flex flex-col gap-4`}
      >
        <span className="text-xl font-bold text-primary">
          {question?.title ?? 'Select all correct answers'}
        </span>

        <div
          className={`w-full ${
            question?.answers[0]?.answerAttachments?.length
              ? `grid w-fit grid-cols-2 gap-3 ${
                  isGridView ? 'desktop:w-2/3' : 'desktop:w-1/2'
                }`
              : 'flex flex-col gap-3'
          }`}
        >
          {question?.answers.map((answer: Answer) => {
            const isSelected = answer.id ? checkboxSelected(answer.id) : false;

            return (
              <div
                key={answer.id}
                className={`group w-full rounded-2xl border-2 bg-theme p-3 text-base font-bold shadow-sm transition-all duration-200 sm:p-4 ${
                  isSelected
                    ? 'border-primary5 !bg-primaryLite shadow-md'
                    : 'border-grey1 hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md'
                } ${
                  disableInput
                    ? 'cursor-not-allowed opacity-70'
                    : 'cursor-pointer active:scale-[0.98]'
                }`}
                role="presentation"
                onClick={() => handleSelect(answer)}
              >
                <div className="flex h-full items-center justify-start gap-3">
                  {/* Checkbox indicator */}
                  <div className="flex h-full shrink-0 items-center">
                    <div
                      className={`flex size-6 items-center justify-center rounded-md border-2 transition-all duration-200 ${
                        isSelected
                          ? 'border-primary bg-primary'
                          : 'border-grey bg-theme group-hover:border-primary'
                      }`}
                    >
                      {isSelected && (
                        <span className="text-sm font-bold text-white">✓</span>
                      )}
                    </div>
                  </div>

                  {/* Answer content */}
                  {answer?.answerAttachments?.length &&
                  answer?.answerAttachments[0]?.type?.toLowerCase() ===
                    'illustration' ? (
                    <>
                      <img
                        src={answer?.answerAttachments[0]?.attachments.url}
                        alt="answer-illustration"
                        className={`size-5/6 min-w-0 rounded-xl object-contain ${
                          !isLoading ? 'block' : 'hidden'
                        }`}
                        onLoad={() => setIsLoading(false)}
                      />
                      {isLoading && <Spinner />}
                    </>
                  ) : (
                    <div className="leading-relaxed text-gray-800">
                      {answer.answer ? safeParseHtml(answer.answer) : ' -- '}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default MSQ;
